/**
 * Pure utility functions for auth session handling.
 * Extracted from AuthProvider for testability and to fix the stale closure bug
 * that caused infinite refresh loops in multi-tab scenarios.
 */

/**
 * Determines whether we need to check the user's onboarding status
 * against the database.
 *
 * Returns true only when:
 * - We have a session but no known userId yet (first load)
 * - The session user changed (different account signed in)
 *
 * Returns false when:
 * - There is no session
 * - The current userId already matches the session user
 */
export const shouldCheckOnboarding = (
  currentUserId: string | null,
  session: { user: { id: string } } | null
): boolean => {
  if (!session) return false;

  return !currentUserId || currentUserId !== session.user.id;
};

/**
 * Determines whether a stale userId from localStorage should be cleared
 * because the session belongs to a different user.
 */
export const isStaleUserId = (currentUserId: string | null, sessionUserId: string): boolean => {
  return Boolean(currentUserId && currentUserId !== sessionUserId);
};

/**
 * Auth events that trigger a full session check (DB query for onboarding).
 * TOKEN_REFRESHED is intentionally excluded — the user identity does not change
 * on a token refresh, so re-querying the database is unnecessary and causes
 * race condition errors during the JWT rotation window in multi-tab scenarios.
 */
const FULL_CHECK_EVENTS = ['INITIAL_SESSION', 'SIGNED_IN', 'USER_UPDATED'] as const;

const SESSION_ONLY_EVENTS = ['TOKEN_REFRESHED'] as const;

export type AuthEventAction = 'full_check' | 'session_only' | 'sign_out' | 'ignore';

export const getAuthEventAction = (event: string): AuthEventAction => {
  if (event === 'SIGNED_OUT') return 'sign_out';
  if ((FULL_CHECK_EVENTS as readonly string[]).includes(event)) return 'full_check';
  if ((SESSION_ONLY_EVENTS as readonly string[]).includes(event)) return 'session_only';

  return 'ignore';
};
